import { Component, EventEmitter, HostBinding, Injector, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  AbstractComponent,
  ChatChannelTypeEnum,
  ChatCommandTypeEnum,
  ChatConversation,
  ChatFavoriteSection,
  ChatMessage,
  ChatNotificationLevelEnum,
  ChatService,
  ChatSocketService,
  ChatUserDisplayMap,
  RoleUserEnum,
} from 'lib-trend-core';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { resolveChatInitials } from '../chat-initials.util';
import { ChatPresenceView } from '../chat-status/chat-status.util';
import { ChatHighlightSegment, buildExcerpt, fuzzyScore, highlightSegments, normalizeSearchText } from '../chat-switcher/chat-fuzzy.util';
import { ChatConfirmDialogComponent } from '../dialogs/chat-confirm-dialog/chat-confirm-dialog.component';
import { stripChatMarkdown } from '../pipes/chat-preview.util';
import { ChatBadgeService } from '../services/chat-badge.service';
import { ChatConversationDialogsService } from '../services/chat-conversation-dialogs.service';
import { ChatDialogService } from '../services/chat-dialog.service';
import * as ChatActions from '../state/chat.actions';
import { bootstrapPendingSelector, favoriteSectionsSelector, presenceViewsSelector, savedIdsSelector } from '../state/chat.selectors';
import { ChatConversationListStateService } from './chat-conversation-list-state.service';

/**
 * Where the list renders.
 * - `popup` — the product's compact bubble: quick links in one row.
 * - `rail`  — a full-height column (the chat app's left rail): vertical shortcuts,
 *   the violet panel painted by the list itself.
 */
export type ChatConversationListVariant = 'popup' | 'rail';

/** Panes the list's shortcuts lead to — the host decides how to show them. */
export type ChatConversationListPane = 'saved' | 'team' | 'threads';

/** A message hit chosen from the list search. */
export interface ChatConversationListMessageHit {
  channelId: string;
  messageId: string;
}

/**
 * What a section IS, which decides the actions its header offers. Typed rather
 * than compared against string keys in the template.
 */
type ChatListSectionKind = 'favorites' | 'channels' | 'groups' | 'directs';

/**
 * One collapsible section of the list: the favourite sections (default plus the
 * user's custom ones) followed by the type sections.
 */
interface ChatListSection {
  key: string;
  kind: ChatListSectionKind;
  /** i18n key when `translateTitle`, literal (custom section name) otherwise. */
  title: string;
  translateTitle: boolean;
  /**
   * i18n key shown in place of the rows when the section is empty. Per section
   * because "no conversation here" is the wrong sentence for Canais, which is
   * empty when no channel EXISTS rather than when none was filed into it.
   */
  emptyKey: string;
  items: ChatListConversationItem[];
  /** Favorite-section id — set on favourite sections only. */
  sectionId?: string;
  /** The auto-created default favourites section: cannot be renamed or deleted. */
  isDefaultSection?: boolean;
  /**
   * A section the user created themselves — the only kind that gets a delete
   * control at all. Decides WHICH header control is rendered; `canDelete` decides
   * whether it is usable.
   */
  isCustomSection: boolean;
  /**
   * Whether this section may be deleted RIGHT NOW — the SINGLE source of that rule,
   * read by the header button's `disabled`, by its tooltip, and by `deleteSection`.
   * True only for a user-created section that is EMPTY: deleting one that still
   * holds conversations would silently relocate them.
   */
  canDelete: boolean;
}

/** Target of a row's "Mover para…" menu. */
interface ChatListSectionOption {
  id: string;
  title: string;
  translateTitle: boolean;
}

interface ChatListConversationItem {
  id: string;
  name: string;
  icon: string;
  image: string;
  initials: string;
  /** Drives the type split: Canais (PUBLIC/PRIVATE) · Grupos · Diretas. */
  type: ChatChannelTypeEnum;
  isDirect: boolean;
  /** The person behind a DIRECT row (myself on the personal-notes one); null otherwise. */
  counterpartId: string | null;
  unread: number;
  /** Favourited (FAVORITE_TOGGLE). Puts the row in a favourite section. */
  favored: boolean;
  /** Which favourite section holds it, for the "Mover para…" check mark. */
  sectionId: string | null;
  /** Notification level is NONE. Kept as the level too, for the rollback path. */
  muted: boolean;
  level: ChatNotificationLevelEnum;
  /**
   * Active membership exists. False on the PUBLIC channels the bootstrap lists
   * company-wide (DEV-1984) — those rows open the read-only preview and hide the
   * membership-backed menu actions (mark unread, notifications).
   */
  isMember: boolean;
}

/** One member of the conversation a message hit came from. */
interface ChatListParticipant {
  id: string;
  name: string;
  image: string;
  initials: string;
}

/** Message hit of the list search — the only kind that needs the backend. */
interface ChatListMessageResult {
  id: string;
  channelId: string;
  /** Which conversation the hit came from, resolved from my own list. */
  contextLabel: string;
  authorName: string;
  /** Excerpt centered on the match, pre-split for `<mark>` rendering. */
  excerptSegments: ChatHighlightSegment[];
  /** Roster stack — GROUP hits only, see `resolveParticipants`. */
  participants: ChatListParticipant[];
  participantsOverflow: number;
  /** Full roster as a phrase, for the stack's tooltip and screen readers. */
  participantsLabel: string;
}

/** Message hits plus the display map the endpoint ships alongside them. */
interface ChatMessageHits {
  messages: ChatMessage[];
  userDisplay: ChatUserDisplayMap;
  /**
   * The term these hits answer. Carried through the pipe instead of read back from
   * `searchTerm` at build time: a fast typist changes the field while the request is
   * in flight, and highlighting a term the server never saw would mark the wrong
   * characters — or none at all.
   */
  term: string;
}

const EMPTY_MESSAGE_HITS: ChatMessageHits = {
  messages: [],
  userDisplay: {},
  term: '',
};

/** Below this the message query is too noisy to be worth a round trip. */
const SEARCH_MIN_LENGTH = 2;

/** Server-side cap is 50 (SEARCH_MAX_LIMIT); the list shows a short list. */
const SEARCH_MESSAGE_LIMIT = 15;

/**
 * Excerpt half-window, in characters — far below the util's default of 70 on
 * purpose. The preview is a single `text-truncate` line and the ellipsis eats the
 * TAIL, so a wide window would push the highlighted match out of view.
 */
const SEARCH_EXCERPT_RADIUS = 24;

/** Avatars in a hit's roster stack before the rest collapses into "+N". */
const MESSAGE_PARTICIPANTS_SHOWN = 3;

/**
 * The conversation list shared by every chat surface — SPEC-2026-09-internal-chat-pwa
 * (T-24 · RF-01 · RF-13). Extracted from the product's bubble host so the chat app's
 * rail renders the SAME list: search (local conversations + server message hits),
 * favourite and type sections, the unread filter, and every row action (mark unread,
 * favourite, move to section, notification level, the admin-only delete).
 *
 * The list never decides what "open" means: it emits the conversation, the message
 * hit or the pane, and the host routes it (the popup swaps its body, the chat app
 * navigates).
 */
@Component({
  selector: 'chat-conversation-list',
  templateUrl: './chat-conversation-list.component.html',
  styleUrls: ['./chat-conversation-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatConversationListComponent extends AbstractComponent implements OnInit, OnChanges {

  @Input() variant: ChatConversationListVariant = 'popup';

  /**
   * Photos only (the chat app's collapsed rail): no toolbar or section headers, and
   * each row is its avatar — the name moves to a tooltip.
   */
  @Input() collapsed = false;

  /** Conversation currently on screen — highlighted in the rail. */
  @Input() activeChannelId: string | null = null;

  /** Pane currently on screen (rail) — its shortcut gets the active-row tint. */
  @Input() activePane: ChatConversationListPane | null = null;

  @Output() readonly conversationSelected = new EventEmitter<string>();

  @Output() readonly messageHitSelected = new EventEmitter<ChatConversationListMessageHit>();

  @Output() readonly paneSelected = new EventEmitter<ChatConversationListPane>();

  @HostBinding('class')
  get hostClass(): string {
    const collapsed = this.collapsed ? ' chat-conversation-list--collapsed' : '';
    return `chat-conversation-list chat-conversation-list--${this.variant}${collapsed}`;
  }

  conversationItems: ChatListConversationItem[] = [];

  /** The rendered list: favourite sections (default + custom), then by type. */
  sections: ChatListSection[] = [];

  /** Destinations of every row's "Mover para…" menu, in the rendered order. */
  sectionOptions: ChatListSectionOption[] = [];

  /** Same three options, same order and labels as every other level picker. */
  readonly notificationLevels: Array<{ level: ChatNotificationLevelEnum; labelKey: string }> = [
    { level: ChatNotificationLevelEnum.ALL, labelKey: 'chat.sidebar.notifyAll' },
    { level: ChatNotificationLevelEnum.MENTIONS, labelKey: 'chat.sidebar.notifyMentions' },
    { level: ChatNotificationLevelEnum.NONE, labelKey: 'chat.sidebar.notifyNone' },
  ];

  /**
   * Platform ADMIN — gates the per-row "Excluir conversa" entry. Checked as
   * `roles.includes(ADMIN)` to mirror the server-side governance guard EXACTLY — not
   * AbstractComponent.isAdmin(), which demands the full ADMIN+SUPERVISOR+ATTENDANT
   * triple.
   */
  isPlatformAdmin = false;

  /** Gates the "Novo canal" action: platform ADMIN or SUPERVISOR (DEV-1984). */
  canCreateChannel = false;

  /** Saved-message counter next to the "Salvas" shortcut. */
  savedCount = 0;

  // --- Search (RF-21) --------------------------------------------------------
  // Conversations and groups filter LOCALLY over the bootstrap snapshot, so they
  // react on every keystroke with no round trip. Only message hits need the
  // server, and those ride the debounced pipe below.

  messageResults: ChatListMessageResult[] = [];

  searchingMessages = false;

  bootstrapPending$: Observable<boolean> = this.store.select(bootstrapPendingSelector);

  private readonly searchTerms = new Subject<string>();

  /**
   * Raw conversations by id, plus the bootstrap display map. Both exist only to
   * resolve the roster of a message hit.
   */
  private conversationsById = new Map<string, ChatConversation>();
  private userDisplay: ChatUserDisplayMap = {};

  /** Cached so `search()` and the filter can re-split without the store. */
  private favoriteSections: ChatFavoriteSection[] = [];

  /** userId → presence view, read at render time by `presenceDotClass`. */
  private presenceViews: Record<string, ChatPresenceView> = {};

  private currentUserId = '';

  constructor(
    injector: Injector,
    private readonly store: Store,
    private readonly chatBadge: ChatBadgeService,
    private readonly chatService: ChatService,
    private readonly chatSocketService: ChatSocketService,
    private readonly dialog: ChatDialogService,
    private readonly conversationDialogs: ChatConversationDialogsService,
    private readonly listState: ChatConversationListStateService,
  ) {
    super(injector);
  }

  /** Raw input value — empty means "not searching" and restores the sections. */
  get searchTerm(): string {
    return this.listState.searchTerm;
  }

  get unreadOnly(): boolean {
    return this.listState.unreadOnly;
  }

  ngOnInit(): void {
    const currentUser = this.getCurrentUserUser();
    this.currentUserId = this.getIDCurrentUser();
    this.isPlatformAdmin = currentUser?.roles?.includes(RoleUserEnum.ADMIN) ?? false;
    this.canCreateChannel = currentUser?.roles?.some(
      (role) => role === RoleUserEnum.ADMIN || role === RoleUserEnum.SUPERVISOR,
    ) ?? false;

    combineLatest([
      this.chatBadge.conversations$,
      this.chatBadge.unreadByChannel$,
      this.chatBadge.userDisplay$,
      this.store.select(favoriteSectionsSelector),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([conversations, unreadByChannel, userDisplay, favoriteSections]) => {
        this.userDisplay = userDisplay;
        this.conversationsById = new Map(conversations.map((conversation) => [conversation._id, conversation]));
        this.favoriteSections = favoriteSections;
        this.conversationItems = this.buildItems(
          conversations,
          unreadByChannel,
          userDisplay,
          this.resolveSectionByChannel(favoriteSections),
        );
        this.buildSections(this.conversationItems);
      });

    this.store
      .select(savedIdsSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((savedIds) => this.savedCount = savedIds.length);

    // Kept apart from the row build: a colleague changing presence or status only
    // repaints their dot, without rebuilding the rows and sections.
    this.store
      .select(presenceViewsSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((views) => this.presenceViews = views);

    // Message hits only — conversations and groups are filtered locally by
    // `search()`. `switchMap` drops the in-flight request when a newer term
    // arrives, so results can never land out of order.
    this.searchTerms
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((term) => {
          if (term.length < SEARCH_MIN_LENGTH) {
            return of<ChatMessageHits>(EMPTY_MESSAGE_HITS);
          }
          return this.chatService.search({ q: term, limit: SEARCH_MESSAGE_LIMIT }).pipe(
            // `userDisplay` travels with the hits: a message can come from a PUBLIC
            // channel I am not a member of, so its author is not necessarily in the
            // bootstrap display map.
            map((envelope) => <ChatMessageHits>{
              messages: envelope?.data?.messages ?? [],
              userDisplay: envelope?.data?.userDisplay ?? {},
              term,
            }),
            // A failed search degrades to "no message hits" — the local
            // conversation results are still on screen and useful.
            catchError(() => of<ChatMessageHits>(EMPTY_MESSAGE_HITS)),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((hits) => {
        this.searchingMessages = false;
        this.messageResults = this.buildMessageResults(hits);
      });

    // A term kept from before the list was last unmounted (the popup minimized, a
    // phone route change) re-runs, so the list comes back as the user left it.
    if (this.searchTerm && !this.collapsed) {
      this.search(this.searchTerm);
    }
  }

  /** Collapsing hides the search box and the unread filter, so the rows are rebuilt without them (and back). */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['collapsed'] && !changes['collapsed'].firstChange) {
      this.buildSections(this.conversationItems);
    }
  }

  // --- Host-facing API ----------------------------------------------------------

  /**
   * Every keystroke: conversations and groups re-filter IMMEDIATELY (local, no
   * request), while the term is pushed to the debounced pipe that fetches message
   * hits. Two speeds on purpose — the list must not wait on the network.
   */
  search(term: string): void {
    this.listState.searchTerm = term ?? '';
    this.buildSections(this.conversationItems);

    const normalized = this.searchTerm.trim();
    if (!normalized) this.messageResults = [];

    // Owned here, not inside the pipe: the request only starts 250ms later, and a
    // flag left "idle" during the debounce window flashes an empty state before the
    // spinner ever appears. The subscription clears it on settle.
    this.searchingMessages = normalized.length >= SEARCH_MIN_LENGTH;
    this.searchTerms.next(normalized);
  }

  clearSearch(): void {
    this.search('');
  }

  // --- Creating conversations ---------------------------------------------------

  openNewDirect(): void {
    this.conversationDialogs.openNewDirect();
  }

  openNewChannel(): void {
    this.conversationDialogs.openNewChannel();
  }

  openBrowseChannels(): void {
    this.conversationDialogs.openBrowseChannels();
  }

  createSection(): void {
    this.conversationDialogs.openNewSection();
  }

  // --- Selection ----------------------------------------------------------------

  openConversation(item: ChatListConversationItem): void {
    // The term meant "find a conversation" — carrying it into the next visit of the
    // list would filter by something the user is no longer asking about.
    this.clearSearch();
    this.conversationSelected.emit(item.id);
  }

  /** Message hit: opens its conversation and reveals that message (RF-21). */
  openMessageResult(result: ChatListMessageResult): void {
    this.clearSearch();
    this.messageHitSelected.emit({ channelId: result.channelId, messageId: result.id });
  }

  openPane(pane: ChatConversationListPane): void {
    this.paneSelected.emit(pane);
  }

  // --- Sections and filter ------------------------------------------------------

  /** Unknown keys are expanded — see `ChatConversationListStateService`. */
  isSectionExpanded(key: string): boolean {
    return !this.listState.collapsedSections.has(key);
  }

  toggleSection(key: string): void {
    if (this.listState.collapsedSections.has(key)) this.listState.collapsedSections.delete(key);
    else this.listState.collapsedSections.add(key);
  }

  trackSection(_index: number, section: ChatListSection): string {
    return section.key;
  }

  toggleUnreadOnly(): void {
    this.listState.unreadOnly = !this.listState.unreadOnly;
    this.buildSections(this.conversationItems);
  }

  /**
   * A filter is on, it has settled, and nothing survived it. Distinct from "no
   * conversations at all", which is a different empty state with a different call to
   * action.
   */
  get noListResults(): boolean {
    if (this.searchingMessages) return false;
    if (!this.searchTerm && !this.unreadOnly) return false;
    // `sections` can be non-empty while holding nothing: an empty custom section
    // always renders. So count the ROWS, not the sections.
    const rows = this.sections.reduce((total, section) => total + section.items.length, 0);
    return rows === 0 && this.messageResults.length === 0;
  }

  /** The unread filter alone found nothing — good news, so it says so. */
  get noUnreadResults(): boolean {
    return this.noListResults && this.unreadOnly && !this.searchTerm;
  }

  // --- Row actions --------------------------------------------------------------
  // Dispatch the optimistic store change, send the socket command, and reconcile on
  // the ack. The authoritative state always arrives back over the socket broadcast.

  markAsUnread(item: ChatListConversationItem): void {
    // Membership-backed command — a non-member public row never offers it, but the
    // guard keeps a stale menu from firing a doomed MARK_UNREAD.
    if (!item.isMember) return;
    this.store.dispatch(ChatActions.setManualUnread({ channelId: item.id }));
    this.chatSocketService
      .sendCommand(ChatCommandTypeEnum.MARK_UNREAD, { channelId: item.id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => { /* authoritative counter arrives on chat:unread:updated */ },
        error: (ack) => this.alertService.error(this.resolveAckError(ack?.errorKey)),
      });
  }

  setNotificationLevel(item: ChatListConversationItem, level: ChatNotificationLevelEnum): void {
    // Levels live on the membership row — nothing to set for a non-member.
    if (!item.isMember) return;
    if (item.level === level) return;
    this.store.dispatch(ChatActions.setNotificationLevel({ channelId: item.id, level }));
    this.chatSocketService
      .sendCommand(ChatCommandTypeEnum.NOTIFICATION_SET_LEVEL, { channelId: item.id, level })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => { /* optimistic update already applied */ },
        error: (ack) => {
          // Roll back to the server-known level on refusal.
          this.store.dispatch(ChatActions.setNotificationLevel({ channelId: item.id, level: item.level }));
          this.alertService.error(this.resolveAckError(ack?.errorKey));
        },
      });
  }

  /**
   * FAVORITE_SECTION_DELETE — its favourites fall back to the default section.
   * Guarded on the same `canDelete` the button's `disabled` is bound to, so the rule
   * holds even if this is ever called from somewhere that forgot to check.
   */
  deleteSection(section: ChatListSection): void {
    if (!section.canDelete || !section.sectionId) {
      if (section.isDefaultSection) {
        this.alertService.error(this.t('chat.errors.defaultSectionImmutable'));
      }
      return;
    }

    this.dialog
      .open(ChatConfirmDialogComponent, { width: '480px', data: this.t('chat.sections.deleteConfirm') })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (confirmed !== true) return;
        this.chatSocketService
          .sendCommand(ChatCommandTypeEnum.FAVORITE_SECTION_DELETE, { sectionId: section.sectionId })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => this.store.dispatch(ChatActions.loadBootstrap()),
            error: (ack) => this.alertService.error(this.resolveAckError(ack?.errorKey)),
          });
      });
  }

  /**
   * Moves a favourite into another section. FAVORITE_TOGGLE has move semantics
   * server-side, so the target must never be the CURRENT section: that would
   * unfavourite it instead of moving it.
   */
  moveToSection(item: ChatListConversationItem, sectionId: string): void {
    if (!item.favored || item.sectionId === sectionId) return;

    this.chatSocketService
      .sendCommand(ChatCommandTypeEnum.FAVORITE_TOGGLE, { channelId: item.id, sectionId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.store.dispatch(ChatActions.loadBootstrap()),
        error: (ack) => this.alertService.error(this.resolveAckError(ack?.errorKey)),
      });
  }

  toggleFavorite(item: ChatListConversationItem): void {
    this.chatSocketService
      .sendCommand(ChatCommandTypeEnum.FAVORITE_TOGGLE, { channelId: item.id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        // Sections are user-scoped server state — re-bootstrap for the truth.
        next: () => this.store.dispatch(ChatActions.loadBootstrap()),
        error: (ack) => this.alertService.error(this.resolveAckError(ack?.errorKey)),
      });
  }

  /**
   * CHANNEL_DELETE straight from the list row — platform ADMIN only, never for
   * DIRECT (the server re-checks both). No optimistic patch: the row disappears for
   * every member through the `deleted` fan-out.
   */
  deleteConversation(item: ChatListConversationItem): void {
    if (!this.isPlatformAdmin || item.isDirect) return;

    this.dialog
      .open(ChatConfirmDialogComponent, { width: '480px', data: this.t('chat.channel.deleteConfirm') })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (confirmed !== true) return;
        this.chatSocketService
          .sendCommand(ChatCommandTypeEnum.CHANNEL_DELETE, { channelId: item.id })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => this.alertService.success(this.t('chat.channel.deleteSuccess')),
            error: (ack) => this.alertService.error(this.resolveAckError(ack?.errorKey)),
          });
      });
  }

  /**
   * Presence dot of a DIRECT row's counterpart — the same view the conversation
   * header renders. On the personal-notes row that is me: online unless the store
   * says otherwise, as every "me" surface reads it.
   */
  presenceDotClass(item: ChatListConversationItem): string {
    const view = this.presenceViews[item.counterpartId ?? ''];
    if (view) return view.dotClass;
    return item.counterpartId === this.currentUserId ? 'bg-success' : 'bg-secondary';
  }

  /** Segments are positional and their text repeats — the index alone is not stable. */
  trackSegment(index: number, segment: ChatHighlightSegment): string {
    return `${index}:${segment.text}`;
  }

  /** Translates a backend/synthetic ack errorKey, falling back when unknown. */
  private resolveAckError(errorKey?: string): string {
    if (errorKey) {
      const translated = this.t(errorKey);
      if (translated !== errorKey) return translated;
    }
    return this.t('chat.errors.commandFailed');
  }

  // ---------------------------------------------------------------------------
  // View-model building
  // ---------------------------------------------------------------------------

  private buildItems(
    conversations: ChatConversation[],
    unreadByChannel: Record<string, number>,
    userDisplay: ChatUserDisplayMap,
    sectionByChannel: Map<string, string>,
  ): ChatListConversationItem[] {
    // Activity order only — favourites have their own section.
    return [...conversations]
      .sort((a, b) => this.activityMillis(b) - this.activityMillis(a))
      .map((conversation) => {
        const name = this.resolveName(conversation, userDisplay);
        const isDirect = conversation.type === ChatChannelTypeEnum.DIRECT;
        // Only DIRECT rows have a person behind them; groups and channels keep their
        // type icon.
        const counterpartId = isDirect ? this.resolveDirectCounterpart(conversation) : null;
        // The store's optimistic `setNotificationLevel` patches this membership, so
        // the icon flips before the ack lands.
        const level = conversation.membership?.notificationLevel ?? ChatNotificationLevelEnum.ALL;
        return {
          id: conversation._id,
          name,
          icon: this.resolveIcon(conversation),
          image: counterpartId ? (userDisplay[counterpartId]?.image ?? '') : '',
          initials: counterpartId ? resolveChatInitials(userDisplay[counterpartId]?.name ?? name) : '',
          type: conversation.type,
          isDirect,
          counterpartId,
          favored: sectionByChannel.has(conversation._id),
          sectionId: sectionByChannel.get(conversation._id) ?? null,
          muted: level === ChatNotificationLevelEnum.NONE,
          level,
          isMember: !!conversation.membership,
          unread: unreadByChannel[conversation._id] ?? 0,
        };
      });
  }

  /**
   * Builds the rendered sections: every favourite section in the user's own order,
   * then the type sections over whatever is left. A conversation appears in exactly
   * ONE section. While searching, the same split applies to the fuzzy-matched subset,
   * ranked by score instead of by activity.
   */
  private buildSections(items: ChatListConversationItem[]): void {
    // The unread filter narrows the pool BEFORE ranking, so a search inside the
    // filter answers "unread conversations matching this". Collapsed, neither
    // control is on screen, so neither narrows the rows; both keep their state.
    const unreadFiltering = this.unreadOnly && !this.collapsed;
    const pool = unreadFiltering ? items.filter((item) => item.unread > 0) : items;

    const term = this.collapsed ? '' : normalizeSearchText(this.searchTerm);
    // Sections keep rendering while a filter is on (their headers carry the create
    // actions), but "no channel created yet" would be a lie there — say "no match".
    const filtering = unreadFiltering || !!term;
    const emptyKey = (key: string) => (filtering ? 'chat.sections.noMatches' : key);
    const visible = term
      ? pool
        .map((item) => ({ item, score: fuzzyScore(item.name, term) }))
        .filter((entry) => entry.score >= 0)
        .sort((a, b) => b.score - a.score)
        .map((entry) => entry.item)
      : pool;

    const visibleById = new Map(visible.map((item) => [item.id, item]));
    const ordered = this.orderedFavoriteSections();

    // Same list, same order, for the rendered sections and every row's "Mover
    // para…" menu.
    this.sectionOptions = ordered.map((section) => ({
      id: section._id,
      title: section.isDefault ? 'chat.sidebar.favorites' : section.name,
      translateTitle: !!section.isDefault,
    }));

    const sections: ChatListSection[] = ordered.map((section) => {
      const entries = [...(section.items ?? [])].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
      const channelIds = entries.map((entry) => this.resolveEntryChannelId(entry));
      const isCustomSection = !section.isDefault;

      return {
        key: `favorites:${section._id}`,
        kind: 'favorites' as const,
        title: section.isDefault ? 'chat.sidebar.favorites' : section.name,
        translateTitle: !!section.isDefault,
        emptyKey: emptyKey('chat.sections.empty'),
        sectionId: section._id,
        isDefaultSection: !!section.isDefault,
        isCustomSection,
        canDelete: isCustomSection && this.isSectionEmpty(channelIds),
        items: channelIds
          .map((channelId) => visibleById.get(channelId))
          .filter((item): item is ChatListConversationItem => !!item),
      };
    });

    // An empty CUSTOM section still renders — otherwise it could never be renamed,
    // deleted, or picked as a destination. The default section hides when empty.
    const favoriteSections = sections.filter((section) => section.items.length > 0 || !section.isDefaultSection);

    const rest = visible.filter((item) => !item.favored);
    const ofType = (...types: ChatChannelTypeEnum[]) => rest.filter((item) => types.includes(item.type));

    // Groupings by conversation type, not stored sections: Canais · Grupos ·
    // Mensagens diretas.
    const typeSections: ChatListSection[] = [
      {
        key: 'channels',
        kind: 'channels' as const,
        title: 'chat.sidebar.channels',
        translateTitle: true,
        emptyKey: emptyKey('chat.sections.emptyChannels'),
        isCustomSection: false,
        canDelete: false,
        items: ofType(ChatChannelTypeEnum.PUBLIC, ChatChannelTypeEnum.PRIVATE),
      },
      {
        key: 'groups',
        kind: 'groups' as const,
        title: 'chat.sidebar.groups',
        translateTitle: true,
        emptyKey: emptyKey('chat.sections.empty'),
        isCustomSection: false,
        canDelete: false,
        items: ofType(ChatChannelTypeEnum.GROUP),
      },
      {
        key: 'directs',
        kind: 'directs' as const,
        title: 'chat.sidebar.directMessages',
        translateTitle: true,
        emptyKey: emptyKey('chat.sections.emptyDirects'),
        isCustomSection: false,
        canDelete: false,
        items: ofType(ChatChannelTypeEnum.DIRECT),
      },
      // "Canais" and "Mensagens diretas" always render: their headers carry the only
      // way to create a channel or start a conversation. "Grupos" hides when empty.
    ].filter((section) => section.kind !== 'groups' || section.items.length > 0);

    this.sections = [...favoriteSections, ...typeSections];
  }

  /**
   * Does this section hold no conversation at all? Measured against the UNFILTERED
   * list on purpose — a section with three conversations can render empty under a
   * search, and deleting it then would relocate conversations the user cannot see.
   */
  private isSectionEmpty(channelIds: string[]): boolean {
    const known = new Set(this.conversationItems.map((item) => item.id));
    return !channelIds.some((channelId) => known.has(channelId));
  }

  /** Default section first, then custom ones by the user's order. */
  private orderedFavoriteSections(): ChatFavoriteSection[] {
    return [...(this.favoriteSections ?? [])].sort((a, b) =>
      a.isDefault === b.isDefault ? (a.order ?? 0) - (b.order ?? 0) : a.isDefault ? -1 : 1,
    );
  }

  /** Channel ids the user favourited, mapped to the section that holds them. */
  private resolveSectionByChannel(favoriteSections: ChatFavoriteSection[]): Map<string, string> {
    const sectionByChannel = new Map<string, string>();
    for (const section of favoriteSections ?? []) {
      for (const entry of section?.items ?? []) {
        const channelId = this.resolveEntryChannelId(entry);
        if (channelId) sectionByChannel.set(channelId, section._id);
      }
    }
    return sectionByChannel;
  }

  /** `channel` is an id on the bootstrap payload; a populated document is tolerated. */
  private resolveEntryChannelId(entry: unknown): string {
    const channel = (entry as { channel?: unknown })?.channel;
    const channelId = typeof channel === 'string' ? channel : (channel as { _id?: string })?._id;
    return channelId ? String(channelId) : '';
  }

  /**
   * Maps server message hits into rows. The conversation NAME comes from my own list,
   * never from the payload: PUBLIC channels I am not a member of have no row here and
   * fall back to the generic channel label.
   */
  private buildMessageResults(hits: ChatMessageHits): ChatListMessageResult[] {
    const nameByChannel = new Map(this.conversationItems.map((item) => [item.id, item.name]));

    return (hits.messages ?? []).map((message) => {
      const channelId = String(message.channel ?? '');
      const senderId = String(message.sender ?? '');
      const participants = this.resolveParticipants(channelId);

      return {
        id: String(message._id),
        channelId,
        contextLabel: nameByChannel.get(channelId) ?? this.t('chat.bubble.channelConversation'),
        authorName: hits.userDisplay[senderId]?.name ?? this.t('chat.saved.unknownAuthor'),
        // Stripped before the window is cut, so markers do not consume the context
        // around the match.
        excerptSegments: highlightSegments(
          buildExcerpt(stripChatMarkdown(message.content), hits.term, SEARCH_EXCERPT_RADIUS),
          hits.term,
        ),
        participants: participants.slice(0, MESSAGE_PARTICIPANTS_SHOWN),
        participantsOverflow: Math.max(0, participants.length - MESSAGE_PARTICIPANTS_SHOWN),
        participantsLabel: participants.length
          ? this.t('chat.bubble.participants', { names: participants.map((participant) => participant.name).join(', ') })
          : '',
      };
    });
  }

  /**
   * Roster of the conversation a hit came from. GROUP only: a DIRECT row already
   * names the counterpart, and channels carry no `members` in the bootstrap payload.
   * Members with no display name are dropped rather than drawn as blank circles.
   */
  private resolveParticipants(channelId: string): ChatListParticipant[] {
    const conversation = this.conversationsById.get(channelId);
    if (conversation?.type !== ChatChannelTypeEnum.GROUP) return [];

    return (conversation.members ?? [])
      .map((id) => {
        const name = this.userDisplay[id]?.name ?? '';
        return { id, name, image: this.userDisplay[id]?.image ?? '', initials: resolveChatInitials(name) };
      })
      .filter((participant) => !!participant.name);
  }

  private activityMillis(conversation: ChatConversation): number {
    const date = conversation.lastMessageAt ?? conversation.createdAt;
    const millis = date ? new Date(date).getTime() : 0;
    return Number.isNaN(millis) ? 0 : millis;
  }

  private resolveName(conversation: ChatConversation, userDisplay: ChatUserDisplayMap): string {
    const currentUserId = this.getIDCurrentUser();

    switch (conversation.type) {
      case ChatChannelTypeEnum.DIRECT:
        return userDisplay[this.resolveDirectCounterpart(conversation)]?.name ?? this.t('chat.bubble.directConversation');
      case ChatChannelTypeEnum.GROUP: {
        if (conversation.name) return conversation.name;
        const names = (conversation.members ?? [])
          .filter((id) => id !== currentUserId)
          .map((id) => userDisplay[id]?.name)
          .filter((name): name is string => !!name);
        return names.length > 0 ? names.slice(0, 3).join(', ') : this.t('chat.bubble.groupConversation');
      }
      default:
        return conversation.name ?? this.t('chat.bubble.channelConversation');
    }
  }

  private resolveIcon(conversation: ChatConversation): string {
    switch (conversation.type) {
      case ChatChannelTypeEnum.DIRECT: return 'ri-user-3-line';
      case ChatChannelTypeEnum.GROUP: return 'ri-group-line';
      case ChatChannelTypeEnum.PRIVATE: return 'ri-lock-line';
      default: return 'ri-hashtag';
    }
  }

  /**
   * The other member of a DIRECT conversation. Falls back to me, which is the self-DM
   * (personal notes) — the only case where "the other member" is myself.
   */
  private resolveDirectCounterpart(conversation: ChatConversation): string {
    const currentUserId = this.getIDCurrentUser();
    return (conversation.members ?? []).find((id) => id !== currentUserId) ?? currentUserId;
  }
}
